import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  TrendingUp,
  AlertTriangle,
  Users,
  Calendar,
  BarChart3,
  Activity,
  ArrowUp,
  ArrowDown,
  Minus,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { apiClient } from "../services/api";

// ─── helpers ────────────────────────────────────────────────────────────────

const RISK_COLORS = {
  critical: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  medium: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  low: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
};

const RiskBadge = ({ level }) => {
  const { t } = useTranslation();
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RISK_COLORS[level] || ""}`}>
      {t(`analyticsHub.risk.${level}`, { defaultValue: level })}
    </span>
  );
};

const ScoreBar = ({ score }) => {
  const color =
    score >= 70 ? "bg-red-500" : score >= 45 ? "bg-orange-400" : score >= 20 ? "bg-yellow-400" : "bg-green-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-sm font-semibold w-10 text-right">{score}</span>
    </div>
  );
};

const KpiCard = ({ title, value, sub, icon: Icon, color = "blue", trend }) => {
  const { t } = useTranslation();
  const colors = {
    blue: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400",
    red: "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400",
    orange: "text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400",
    green: "text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400",
    purple: "text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400",
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${colors[color]}`}>
          <Icon size={20} />
        </div>
      </div>
      {trend !== undefined && (
        <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
          {trend > 0 ? <ArrowUp size={12} className="text-red-500" /> : trend < 0 ? <ArrowDown size={12} className="text-green-500" /> : <Minus size={12} />}
          <span>{Math.abs(trend)} {t("analyticsHub.vsLastMonth")}</span>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Tableau de bord ────────────────────────────────────────────────────

const DashboardTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/analytics/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">{t("analyticsHub.loadError")}</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title={t("analyticsHub.dashboard.kpiActive")} value={data.total_active_employees} icon={Users} color="blue" />
        <KpiCard title={t("analyticsHub.dashboard.kpiCritical")} value={data.turnover_risk.critical_count} sub={t("analyticsHub.dashboard.kpiCriticalSub")} icon={AlertTriangle} color="red" />
        <KpiCard title={t("analyticsHub.dashboard.kpiHigh")} value={data.turnover_risk.high_count} sub={t("analyticsHub.dashboard.kpiHighSub")} icon={TrendingUp} color="orange" />
        <KpiCard title={t("analyticsHub.dashboard.kpiAvgScore")} value={`${data.turnover_risk.avg_score}/100`} sub={t("analyticsHub.dashboard.kpiAvgScoreSub")} icon={Activity} color="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <KpiCard title={t("analyticsHub.dashboard.kpiAbsence")} value={`${(data.absence_rate_30d * 100).toFixed(1)}%`} icon={Calendar} color="orange" />
        <KpiCard title={t("analyticsHub.dashboard.kpiNewHires")} value={data.new_hires_90d} icon={ArrowUp} color="green" />
        <KpiCard title={t("analyticsHub.dashboard.kpiDepartures")} value={data.departures_90d} icon={ArrowDown} color="red" />
      </div>

      {data.top_risk_employees?.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2"><AlertTriangle size={16} className="text-red-500" /> {t("analyticsHub.dashboard.topRiskTitle")}</h3>
          </div>
          <div className="divide-y divide-border">
            {data.top_risk_employees.map((emp) => (
              <div key={emp.employee_id} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{emp.employee_name}</p>
                  <p className="text-xs text-muted-foreground">{emp.department} · {emp.contract_type || "—"}</p>
                </div>
                <div className="w-32 hidden sm:block">
                  <ScoreBar score={emp.score} />
                </div>
                <RiskBadge level={emp.risk_level} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Risque Turnover ────────────────────────────────────────────────────

const TurnoverTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = filter !== "all" ? `?risk_level=${filter}` : "";
    apiClient.get(`v1/analytics/turnover-risk${params}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [filter]);

  const reload = () => {
    setLoading(true);
    const params = filter !== "all" ? `?risk_level=${filter}` : "";
    apiClient.get(`v1/analytics/turnover-risk${params}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  const FILTERS = [
    { key: "all", label: t("analyticsHub.turnover.filterAll") },
    { key: "critical", label: t("analyticsHub.risk.critical") },
    { key: "high", label: t("analyticsHub.risk.high") },
    { key: "medium", label: t("analyticsHub.risk.medium") },
    { key: "low", label: t("analyticsHub.risk.low") },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={reload} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <RefreshCw size={14} /> {t("common.refresh")}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/30">
            <p className="text-sm text-muted-foreground">{t("analyticsHub.turnover.analyzed", { count: data?.total })}</p>
          </div>
          <div className="divide-y divide-border">
            {data?.items?.map((emp) => (
              <div key={emp.employee_id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{emp.employee_name}</p>
                      <RiskBadge level={emp.risk_level} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{emp.department} · {emp.job_title || "—"} · {emp.contract_type || "—"}</p>
                  </div>
                  <div className="w-40 hidden sm:block mt-1">
                    <ScoreBar score={emp.score} />
                  </div>
                </div>
                {emp.factors?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {emp.factors.map((f, i) => (
                      <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${f.weight > 0 ? "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400" : "bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400"}`}>
                        {f.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {data?.items?.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">{t("analyticsHub.turnover.empty")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Absences ───────────────────────────────────────────────────────────

const AbsenceTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient.get(`v1/analytics/absence-heatmap?months=${months}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [months]);

  const maxVal = data ? Math.max(1, ...data.departments.flatMap((d) => data.data[d] || [])) : 1;

  const cellColor = (val) => {
    const pct = val / maxVal;
    if (pct === 0) return "bg-muted/30";
    if (pct < 0.25) return "bg-blue-100 dark:bg-blue-900/20";
    if (pct < 0.5) return "bg-orange-100 dark:bg-orange-900/20";
    if (pct < 0.75) return "bg-orange-300 dark:bg-orange-700/40";
    return "bg-red-400 dark:bg-red-600/50";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-muted-foreground">{t("analyticsHub.absences.periodLabel")}</span>
        {[3, 6, 12].map((m) => (
          <button key={m} onClick={() => setMonths(m)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${months === m ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
            {t("analyticsHub.monthsN", { count: m })}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : !data?.departments?.length ? (
        <div className="text-center py-16 text-muted-foreground">{t("analyticsHub.absences.noData")}</div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2"><Calendar size={16} /> {t("analyticsHub.absences.heatmapTitle")}</h3>
          </div>
          <div className="overflow-x-auto p-4">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground w-40">{t("analyticsHub.absences.colDept")}</th>
                  {data.months.map((m) => (
                    <th key={m} className="text-center py-2 px-1 font-medium text-muted-foreground min-w-[60px]">{m.slice(5)}/{m.slice(2, 4)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.departments.map((dept) => (
                  <tr key={dept}>
                    <td className="py-2 pr-4 font-medium truncate max-w-[160px]" title={dept}>{dept}</td>
                    {(data.data[dept] || []).map((val, i) => (
                      <td key={i} className="py-1 px-1">
                        <div className={`rounded text-center py-1 text-xs font-medium ${cellColor(val)}`}>
                          {val > 0 ? val : ""}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 border-t border-border flex items-center gap-3 text-xs text-muted-foreground">
            <span>{t("analyticsHub.absences.legend")}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-100 dark:bg-blue-900/20 inline-block" /> {t("analyticsHub.absences.legendLow")}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-100 dark:bg-orange-900/20 inline-block" /> {t("analyticsHub.absences.legendMedium")}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 dark:bg-red-600/50 inline-block" /> {t("analyticsHub.absences.legendHigh")}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Prévisions ─────────────────────────────────────────────────────────

const ForecastTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient.get(`v1/analytics/headcount-forecast?months_ahead=${months}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [months]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-muted-foreground">{t("analyticsHub.forecast.horizonLabel")}</span>
        {[3, 6, 12].map((m) => (
          <button key={m} onClick={() => setMonths(m)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${months === m ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
            {t("analyticsHub.monthsN", { count: m })}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard title={t("analyticsHub.forecast.kpiCurrent")} value={data.current_headcount} icon={Users} color="blue" />
            <KpiCard title={t("analyticsHub.forecast.kpiHiresMonth")} value={`+${data.avg_monthly_hires}`} icon={ArrowUp} color="green" />
            <KpiCard title={t("analyticsHub.forecast.kpiDeparturesMonth")} value={`-${data.avg_monthly_departures}`} icon={ArrowDown} color="red" />
            <KpiCard title={t("analyticsHub.forecast.kpiNetGrowth")} value={data.net_monthly_growth > 0 ? `+${data.net_monthly_growth}` : data.net_monthly_growth} icon={TrendingUp} color={data.net_monthly_growth >= 0 ? "green" : "red"} />
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="font-semibold flex items-center gap-2"><BarChart3 size={16} /> {t("analyticsHub.forecast.title", { count: months })}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("analyticsHub.forecast.colMonth")}</th>
                    <th className="text-right px-5 py-3 font-medium text-muted-foreground">{t("analyticsHub.forecast.colForecast")}</th>
                    <th className="text-right px-5 py-3 font-medium text-muted-foreground">{t("analyticsHub.forecast.colHires")}</th>
                    <th className="text-right px-5 py-3 font-medium text-muted-foreground">{t("analyticsHub.forecast.colDepartures")}</th>
                    <th className="text-right px-5 py-3 font-medium text-muted-foreground">{t("analyticsHub.forecast.colVariation")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.forecast.map((row, i) => {
                    const prev = i === 0 ? data.current_headcount : data.forecast[i - 1].headcount;
                    const delta = row.headcount - prev;
                    return (
                      <tr key={row.month} className="hover:bg-muted/20">
                        <td className="px-5 py-3 font-medium">{row.month}</td>
                        <td className="px-5 py-3 text-right font-semibold">{row.headcount}</td>
                        <td className="px-5 py-3 text-right text-green-600 dark:text-green-400">+{row.projected_hires}</td>
                        <td className="px-5 py-3 text-right text-red-600 dark:text-red-400">-{row.projected_departures}</td>
                        <td className="px-5 py-3 text-right">
                          <span className={`flex items-center justify-end gap-1 ${delta >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                            {delta >= 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                            {Math.abs(delta)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Équité salariale ────────────────────────────────────────────────────

const GaugeMini = ({ value, max = 100, color }) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const bg = color || (pct >= 70 ? "#22c55e" : pct >= 40 ? "#f59e0b" : "#ef4444");
  return (
    <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
      <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: bg }} />
    </div>
  );
};

const ComplianceRing = ({ score }) => {
  const { t } = useTranslation();
  const color = score >= 80 ? "#22c55e" : score >= 55 ? "#3b82f6" : score >= 30 ? "#f59e0b" : "#ef4444";
  const label = score >= 80 ? t("analyticsHub.payEquity.compConforme") : score >= 55 ? t("analyticsHub.payEquity.compPartiel") : score >= 30 ? t("analyticsHub.payEquity.compRisque") : t("analyticsHub.payEquity.compNonConforme");
  const r = 44, circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative inline-flex items-center justify-center">
        <svg width="110" height="110" className="-rotate-90">
          <circle cx="55" cy="55" r={r} fill="none" stroke="currentColor" strokeWidth="9" className="text-muted/20" />
          <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="9"
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
        </svg>
        <div className="absolute text-center">
          <span className="text-2xl font-bold">{score}</span>
          <span className="block text-[10px] text-muted-foreground">/100</span>
        </div>
      </div>
      <span className="text-xs font-medium" style={{ color }}>{label}</span>
    </div>
  );
};

const PayEquityTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("overview");

  const load = () => {
    setLoading(true);
    apiClient.get("v1/analytics/pay-equity")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">{t("analyticsHub.loadError")}</div>;

  const { gender_summary, gender_pay_gap_pct, departments, job_titles, outliers, compliance_score, compliance_details } = data;
  const gapColor = Math.abs(gender_pay_gap_pct) < 5 ? "text-green-600" : Math.abs(gender_pay_gap_pct) < 10 ? "text-yellow-600" : "text-red-600";

  return (
    <div className="space-y-6">
      {/* EU Pay Transparency notice */}
      <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-xs text-blue-800 dark:text-blue-300">
        <strong>{t("analyticsHub.payEquity.noticeStrong")}</strong>{t("analyticsHub.payEquity.noticeText")}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-5 flex flex-col items-center justify-center">
          <ComplianceRing score={compliance_score} />
          <p className="text-xs text-muted-foreground mt-2">{t("analyticsHub.payEquity.kpiComplianceScore")}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <p className="text-sm text-muted-foreground mb-1">{t("analyticsHub.payEquity.kpiPayGap")}</p>
          <p className={`text-3xl font-bold ${gapColor}`}>{gender_pay_gap_pct > 0 ? "+" : ""}{gender_pay_gap_pct}%</p>
          <p className="text-xs text-muted-foreground mt-1">
            {Math.abs(gender_pay_gap_pct) < 5 ? t("analyticsHub.payEquity.payGapWithin") : t("analyticsHub.payEquity.payGapAbove")}
          </p>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <p className="text-sm text-muted-foreground mb-1">{t("analyticsHub.payEquity.kpiSalaryCoverage")}</p>
          <p className="text-3xl font-bold">{compliance_details.salary_coverage_pct}%</p>
          <GaugeMini value={compliance_details.salary_coverage_pct} />
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <p className="text-sm text-muted-foreground mb-1">{t("analyticsHub.payEquity.kpiAnomalies")}</p>
          <p className={`text-3xl font-bold ${outliers.length > 0 ? "text-red-600 dark:text-red-400" : "text-green-600"}`}>
            {outliers.length}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{t("analyticsHub.payEquity.kpiAnomaliesSub")}</p>
        </div>
      </div>

      {/* Sub-view tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: "overview", label: t("analyticsHub.payEquity.viewOverview") },
          { id: "departments", label: t("analyticsHub.payEquity.viewDepartments") },
          { id: "jobs", label: t("analyticsHub.payEquity.viewJobs") },
          { id: "outliers", label: t("analyticsHub.payEquity.viewOutliers", { count: outliers.length }) },
        ].map((v) => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === v.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
            {v.label}
          </button>
        ))}
        <button onClick={load} className="ml-auto flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <RefreshCw size={14} /> {t("common.refresh")}
        </button>
      </div>

      {/* Overview: gender breakdown */}
      {view === "overview" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-semibold">{t("analyticsHub.payEquity.genderTitle")}</h3>
          </div>
          <div className="divide-y divide-border">
            {Object.entries(gender_summary).map(([g, stats]) => (
              <div key={g} className="px-5 py-4 flex items-center gap-6">
                <span className="font-medium w-32">{g === "M" ? t("analyticsHub.payEquity.male") : g === "F" ? t("analyticsHub.payEquity.female") : g}</span>
                <div className="flex-1 grid grid-cols-3 gap-4 text-sm">
                  <div><p className="text-muted-foreground text-xs">{t("analyticsHub.payEquity.colCount")}</p><p className="font-semibold">{stats.count}</p></div>
                  <div><p className="text-muted-foreground text-xs">{t("analyticsHub.payEquity.colAvgSalary")}</p><p className="font-semibold">{stats.mean.toLocaleString()}</p></div>
                  <div><p className="text-muted-foreground text-xs">{t("analyticsHub.payEquity.colMedianSalary")}</p><p className="font-semibold">{stats.median.toLocaleString()}</p></div>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-border bg-muted/20 text-xs text-muted-foreground">
            {t("analyticsHub.payEquity.genderCoverage", { pct: compliance_details.gender_data_coverage_pct })}
          </div>
        </div>
      )}

      {/* Departments */}
      {view === "departments" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>
                  {[t("analyticsHub.payEquity.deptColDept"), t("analyticsHub.payEquity.deptColHeadcount"), t("analyticsHub.payEquity.deptColM"), t("analyticsHub.payEquity.deptColF"), t("analyticsHub.payEquity.deptColAvgM"), t("analyticsHub.payEquity.deptColAvgF"), t("analyticsHub.payEquity.deptColGap"), t("analyticsHub.payEquity.deptColAvg")].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {Object.entries(departments)
                  .sort(([, a], [, b]) => b.headcount - a.headcount)
                  .map(([dept, d]) => (
                    <tr key={dept} className="hover:bg-muted/20">
                      <td className="px-4 py-3 font-medium">{dept}</td>
                      <td className="px-4 py-3">{d.headcount}</td>
                      <td className="px-4 py-3 text-blue-600">{d.male_count}</td>
                      <td className="px-4 py-3 text-pink-600">{d.female_count}</td>
                      <td className="px-4 py-3">{d.male_mean ? d.male_mean.toLocaleString() : "—"}</td>
                      <td className="px-4 py-3">{d.female_mean ? d.female_mean.toLocaleString() : "—"}</td>
                      <td className="px-4 py-3">
                        {d.gender_pay_gap_pct !== null ? (
                          <span className={`font-semibold ${Math.abs(d.gender_pay_gap_pct) >= 10 ? "text-red-600" : Math.abs(d.gender_pay_gap_pct) >= 5 ? "text-yellow-600" : "text-green-600"}`}>
                            {d.gender_pay_gap_pct > 0 ? "+" : ""}{d.gender_pay_gap_pct}%
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3">{d.avg_salary.toLocaleString()}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Job titles */}
      {view === "jobs" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr>
                  {[t("analyticsHub.payEquity.jobColJob"), t("analyticsHub.payEquity.jobColHeadcount"), t("analyticsHub.payEquity.jobColMin"), t("analyticsHub.payEquity.jobColMedian"), t("analyticsHub.payEquity.jobColMean"), t("analyticsHub.payEquity.jobColMax"), t("analyticsHub.payEquity.jobColSpread")].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {job_titles.map((j) => (
                  <tr key={j.job_title} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium">{j.job_title}</td>
                    <td className="px-4 py-3">{j.headcount}</td>
                    <td className="px-4 py-3 text-green-600">{j.min.toLocaleString()}</td>
                    <td className="px-4 py-3 font-semibold">{j.median.toLocaleString()}</td>
                    <td className="px-4 py-3">{j.mean.toLocaleString()}</td>
                    <td className="px-4 py-3 text-red-600">{j.max.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <GaugeMini value={j.spread_pct} max={100} color={j.spread_pct > 50 ? "#ef4444" : j.spread_pct > 25 ? "#f59e0b" : "#22c55e"} />
                        <span className="text-xs w-10 text-right">{j.spread_pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Outliers */}
      {view === "outliers" && (
        outliers.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-lg font-medium text-green-600">{t("analyticsHub.payEquity.noOutliers")}</p>
            <p className="text-sm mt-1">{t("analyticsHub.payEquity.noOutliersSub")}</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-red-50 dark:bg-red-950/20">
              <p className="text-sm font-medium text-red-700 dark:text-red-400">
                {t("analyticsHub.payEquity.outliersBanner", { count: outliers.length })}
              </p>
            </div>
            <div className="divide-y divide-border">
              {outliers.map((o) => (
                <div key={o.employee_id} className="px-5 py-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{o.employee_name}</p>
                    <p className="text-xs text-muted-foreground">{o.department} · {o.job_title} · {t("analyticsHub.payEquity.genderLabel", { gender: o.gender })}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-red-600 dark:text-red-400 font-semibold">{o.salary.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{t("analyticsHub.payEquity.medianLabel", { value: o.peer_median.toLocaleString() })}</p>
                  </div>
                  <div className="w-16 text-center">
                    <span className="text-xs font-bold text-red-600 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded-full">
                      -{o.gap_pct}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
};

// ─── Hub ────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "dashboard", icon: BarChart3 },
  { id: "turnover", icon: AlertTriangle },
  { id: "absences", icon: Calendar },
  { id: "forecast", icon: TrendingUp },
  { id: "payequity", icon: Users },
];

export default function AnalyticsHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";

  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "turnover":  return <TurnoverTab />;
      case "absences":  return <AbsenceTab />;
      case "forecast":  return <ForecastTab />;
      case "payequity": return <PayEquityTab />;
      default:          return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp size={24} className="text-primary" />
            {t("analyticsHub.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("analyticsHub.subtitle")}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit flex-wrap">
        {TABS.map(({ id, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon size={15} />
            {t(`analyticsHub.tabs.${id}`)}
          </button>
        ))}
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
}
